import { useEffect, useRef, useState } from 'react';
import { triggerHaptic } from './capacitorBridge';

export type BackHandlerResult = boolean | void | Promise<any>;
export type BackHandlerFn = () => BackHandlerResult;

/**
 * LIFO Geri Yığını: Açılan modallar ve ekranlar en üstten geriye doğru sırayla kapatılır.
 */
const stack: Array<BackHandlerFn> = [];

/**
 * Tam ekran oyun/sayfalar ve modallar için geri tuşu/jesti dinleyicisi.
 * Handler false dönerse, eylemi tüketmemiş sayılır ve yığındaki bir önceki işleyiciye geçer.
 * void / true dönerse eylemi tüketir ve kök çıkış uyarısı tetiklenmez.
 */
export function useBackHandler(handler: BackHandlerFn, active = true): void {
    const ref = useRef(handler);
    ref.current = handler;
    useEffect(() => {
        if (!active) return;
        const fn: BackHandlerFn = () => ref.current();
        stack.push(fn);
        return () => {
            const i = stack.lastIndexOf(fn);
            if (i >= 0) stack.splice(i, 1);
        };
    }, [active]);
}

/** 
 * En üstteki geri işleyicisini çalıştırır.
 * Tüketildiyse true döner; kök ekranda tüketilmeyen bir durum varsa false döner.
 */
export function consumeBack(): boolean {
    for (let i = stack.length - 1; i >= 0; i--) {
        const fn = stack[i];
        try {
            const res = fn();
            // Eğer işleyici açıkça false döndürdüyse bir alttaki işleyiciye pasla
            if (res !== false) {
                return true;
            }
        } catch (e) {
            console.warn('[BackHandler] Geri işleyicisi hatası:', e);
            return true;
        }
    }
    return false;
}

export interface EdgeSwipeState {
    active: boolean;
    progress: number;
    y: number;
    reached: boolean;
}

/**
 * Ekranın sol kenarından sağa doğru kaydırma (iOS & Android Edge Swipe-Back) jest dinleyicisi.
 * Sol kenardan (ilk 40px) parmakla sağa çekildiğinde çalışır.
 */
export function useEdgeSwipeBack(onBackTrigger?: () => void, disabled = false): EdgeSwipeState {
    const [swipeState, setSwipeState] = useState<EdgeSwipeState>({
        active: false,
        progress: 0,
        y: 0,
        reached: false,
    });

    const stateRef = useRef(swipeState);
    stateRef.current = swipeState;

    const onBackRef = useRef(onBackTrigger);
    onBackRef.current = onBackTrigger;

    useEffect(() => {
        if (disabled || typeof window === 'undefined') return;

        let startX = 0;
        let startY = 0;
        let startTime = 0;
        let isTracking = false;

        const onTouchStart = (e: TouchEvent) => {
            if (e.touches.length !== 1) return;
            const touch = e.touches[0];

            // Sol kenardan en fazla 42px mesafede başlamalı (standart mobil kenar eşiği)
            if (touch.clientX > 42) return;

            // Çizim tuvali veya kaydırma çubukları üzerinde ise jest başlatma
            const target = e.target as HTMLElement | null;
            if (target?.closest('canvas, .no-swipe-back, input[type="range"], [data-no-swipe="true"]')) {
                return;
            }

            startX = touch.clientX;
            startY = touch.clientY;
            startTime = Date.now();
            isTracking = true;

            setSwipeState({
                active: true,
                progress: 0,
                y: touch.clientY,
                reached: false,
            });
        };

        const onTouchMove = (e: TouchEvent) => {
            if (!isTracking || e.touches.length !== 1) return;
            const touch = e.touches[0];
            const deltaX = touch.clientX - startX;
            const deltaY = Math.abs(touch.clientY - startY);

            // Dikey kaydırma çok fazlaysa jesti iptal et (kullanıcı listeyi kaydırıyor demektir)
            if (deltaY > 55 && deltaX < 30) {
                isTracking = false;
                setSwipeState({ active: false, progress: 0, y: 0, reached: false });
                return;
            }

            if (deltaX > 0) {
                const progress = Math.min(1, Math.max(0, deltaX / 75));
                const reached = deltaX >= 70;
                setSwipeState({
                    active: true,
                    progress,
                    y: touch.clientY,
                    reached,
                });
            }
        };

        const onTouchEnd = () => {
            if (!isTracking) return;
            const current = stateRef.current;
            isTracking = false;

            if (current.reached && (Date.now() - startTime < 800)) {
                triggerHaptic();
                if (onBackRef.current) {
                    onBackRef.current();
                } else {
                    consumeBack();
                }
            }

            setSwipeState({ active: false, progress: 0, y: 0, reached: false });
        };

        const onTouchCancel = () => {
            isTracking = false;
            setSwipeState({ active: false, progress: 0, y: 0, reached: false });
        };

        window.addEventListener('touchstart', onTouchStart, { passive: true });
        window.addEventListener('touchmove', onTouchMove, { passive: true });
        window.addEventListener('touchend', onTouchEnd, { passive: true });
        window.addEventListener('touchcancel', onTouchCancel, { passive: true });

        return () => {
            window.removeEventListener('touchstart', onTouchStart);
            window.removeEventListener('touchmove', onTouchMove);
            window.removeEventListener('touchend', onTouchEnd);
            window.removeEventListener('touchcancel', onTouchCancel);
        };
    }, [disabled]);

    return swipeState;
}
